import type { ReactNode } from "react";
import React, { useMemo } from "react";
import {
  useNavigation,
  usePathname,
  usePathParams,
  useSearchParams,
} from "@edreamsodigeo/frontend-router";

import type {
  QueryParameters,
  Router,
} from "../navigation/router/UseRouterContext";
import { UseRouterContext } from "../navigation/router/UseRouterContext";
import { useBasePath } from "./BasePathProvider";

interface MFERouterProviderProps {
  children: ReactNode;
}

const createNavigateMethod =
  (basePath: string, navigate: (path: string) => void) => (path: string) => {
    navigate(
      path.startsWith("http") || path.startsWith("/") ? path : basePath + path,
    );
  };

const useBaseRouter = () => {
  const basePath = useBasePath();
  const pathname = usePathname();
  const { push, replace, back } = useNavigation();
  return useMemo(
    () => ({
      pathname: pathname.startsWith(basePath)
        ? pathname.substring(basePath.length)
        : pathname,
      push: createNavigateMethod(basePath, push),
      replace: createNavigateMethod(basePath, replace),
      back,
    }),
    [basePath, pathname, push, replace, back],
  );
};

const useQueryProps = <Query extends QueryParameters>() => {
  const { pathParams } = usePathParams();
  const { searchParams } = useSearchParams();
  return useMemo(
    () =>
      ({
        ...searchParams,
        ...pathParams,
      }) as Query,
    [searchParams, pathParams],
  );
};

const useMFERouter = <Query extends QueryParameters>(): Router<Query> => {
  const baseRouter = useBaseRouter();
  const query = useQueryProps<Query>();
  return useMemo(
    () => ({
      ...baseRouter,
      query,
    }),
    [baseRouter, query],
  );
};

export const MFERouterProvider = ({ children }: MFERouterProviderProps) => {
  return (
    <UseRouterContext.Provider value={useMFERouter}>
      {children}
    </UseRouterContext.Provider>
  );
};
